import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
	Button,
	Confirm,
	Modal,
	Select,
	SpinnerOverlay,
} from '@components/common';

import { useUsers } from '@hooks/useUsers';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { generateSelectOptions } from '@utils/generateSelectOptions/generateSelectOptions';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IShortUserInfo, IUserEntity } from '@interfaces/user/user';

import { EUserRoles } from '@enums/common';

import styles from './SwapUserReferenceForm.module.scss';

interface ISwapUserReferenceFormProps {
	closeForm: () => void;
	currentUser: IUserEntity;
	organisationId?: string;
	setIsDirtyForm: Dispatch<SetStateAction<boolean>>;
}

const SwapUserReferenceForm = ({
	closeForm,
	currentUser,
	organisationId,
	setIsDirtyForm: setDirtyForm,
}: ISwapUserReferenceFormProps) => {
	const {
		control,
		watch,
		getValues,
		formState: { isDirty },
	} = useForm();

	const { user } = useAppSelector(auth);

	const [isConfirmModal, setIsConfirmModal] = useState(false);
	const [isAssignButtonAvailable, setIsAssignButtonAvailable] = useState(false);

	const toggleConfirmModal = () => setIsConfirmModal(!isConfirmModal);
	const isPlatformOwner = user?.roles?.some((role) => role === EUserRoles.platformOwnerAdministrator);

	const {
		isSwappingUserReference,
		swapUserReference,
		swapUsersDropdownData,
	} = useUsers({
		organizationId: currentUser?.organization?.id || organisationId,
		isPlatformOwner,
		closeModals: () => {
			toggleConfirmModal();
			closeForm();
		},
	});

	const [usersOptions, setUsersOptions] = useState<Array<TSelectOption>>([]);

	const [ConfirmationText, setConfirmationText] = useState<ReactElement | null>(null);

	const fullName = `${currentUser.firstName} ${currentUser.lastName}`;

	const onSubmit = () => {
		const newUser = getValues('to').value;
		const oldUser = currentUser.id;

		swapUserReference({
			newUser: newUser,
			oldUser: oldUser,
			organizationId: organisationId,
		} as any);
	};

	useEffect(() => setDirtyForm(isDirty), [isDirty, setDirtyForm]);

	useEffect(() => {
		const subscription = watch((value) => {
			setIsAssignButtonAvailable(!!value.to.label);
			setConfirmationText((
				<div>
					<b>{fullName}</b>
					&nbsp;account will be blocked. All his account information will be available to&nbsp;
					<b>{value.to.label}</b>.
				</div>
			));
		});

		return () => subscription.unsubscribe();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watch]);

	useEffect(() => {
		if (swapUsersDropdownData.length) {
			const filteredUsers = swapUsersDropdownData.filter((user: IShortUserInfo) => (user.id !== currentUser.id));
			const options = generateSelectOptions({
				data: filteredUsers,
				label: ['firstName', 'lastName'],
				value: 'id',
				sort: true,
			});

			setUsersOptions(options);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [swapUsersDropdownData]);

	return (
		<div className={styles.container}>
			{isSwappingUserReference && <SpinnerOverlay />}
			<div className={styles.currentUserContainer}>
				<div className={styles.label}>From</div>
				<div className={styles.currentUser}>{fullName}</div>
			</div>
			<div className={styles.swapUserContainer}>
				<Select
					control={control}
					label='To'
					name='to'
					options={usersOptions}
					placeholder='Select user'
				/>
			</div>
			<Button
				containerClassName={styles.assignButton}
				disabled={!isAssignButtonAvailable}
				onClick={toggleConfirmModal}
				text='Assign'
			/>
			<Modal
				isOpen={isConfirmModal}
				onClose={toggleConfirmModal}
				width='520px'
			>
				<Confirm
					isLoading={isSwappingUserReference}
					onClose={toggleConfirmModal}
					onSubmit={onSubmit}
					question='Are you sure you want to reassign account?'
				>
					{ConfirmationText}
				</Confirm>
			</Modal>
		</div>
	);
};

export default SwapUserReferenceForm;
